import React, { memo, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { HandleModalProps } from "@/types/props";
import { emptyScholarship } from "@/models/empty";
import { iScholarship } from "@/types/models";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ZodError } from "../ZodDisplayError";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { CalendarIcon, ChevronDown, RefreshCw } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { classificationScholarship, majorListForScholarship } from "../validations/shared";
import { scholarshipSchema, tScholarshipSchema } from "../validations/scholarship";
import { getLocalDate, toCapitalCaseSafe } from "@/lib/utils/func";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { NgocInput } from "../ui/ngocInput";
import { cn } from "../ui/utils";
import { scholarshipTitleApi } from "@/lib/api/scholarship-title";

export const ScholarshipModal = memo(({ isOpen, onClose, onAdd, onUpdate, editData, mode }: HandleModalProps) => {
	const {
		control,
		register,
		handleSubmit,
		reset,
		trigger,
		setValue,
		getValues,
		formState: { errors, isValid },
	} = useForm<tScholarshipSchema>({
		resolver: zodResolver(scholarshipSchema),
		defaultValues: emptyScholarship,
		mode: "all",
	});

	const submit = (data: tScholarshipSchema) => {
		switch (mode) {
			case "create":
				onAdd(data as Omit<iScholarship, "id">);
				break;
			case "edit":
				onUpdate(data);
				break;
		}
		onClose();
	};
	const [openDateOfBirthSelected, setOpenDateOfBirthSelected] = useState(false);
	const [selectedTitle, setSelectedTitle] = useState<string>();
	const [searchMajorName, setSearchMajorName] = useState("");
	const [searchScholarshipTitleName, setSearchScholarshipTitleName] = useState("");
	const [scholarshipTitleOptions, setScholarshipTitleOptions] = useState<string[]>([]);

	useEffect(() => {
		//= Lấy danh sách các danh hiệu khen thưởng
		(async () => {
			const result = await scholarshipTitleApi.getAll();
			if (result && result.success && result.data) {
				setScholarshipTitleOptions([...result.data.data.map(item => item.titleName)
					// , 'Khác'
				]);
			}
		})();
	}, []);

	useEffect(() => {
		switch (mode) {
			case "create":
				reset(emptyScholarship);
				setSelectedTitle(undefined);
				break;
			case "edit": {
				reset({
					...editData,
					currentMajorCode: editData.majorCode,
					studentName: toCapitalCaseSafe(editData.studentName),
					currentCertificateNo: editData.certificateNo,
					dateOfBirth: editData.dateOfBirth ? getLocalDate(editData.dateOfBirth) : emptyScholarship.dateOfBirth,
					notes: editData.notes || "",
				});
				setSelectedTitle(editData.title);
				trigger();
				break;
			}
		}
		setSearchMajorName("");
		setSearchScholarshipTitleName("");
	}, [isOpen, editData, mode, reset, trigger]);

	const logError = (e: any) => {
		console.log(e);
	};



	return (
		<Dialog
			open={isOpen}
			onOpenChange={() => {
				onClose();
			}}
		>
			<DialogContent className='max-w-6xl'>
				<DialogHeader>
					<DialogTitle>{mode === "create" ? "Thêm Mới" : "Cập Nhật Khen thưởng - Chứng nhận"}</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSubmit(submit, logError)} id='handleScholarshipForm' className='grid grid-cols-1 md:grid-cols-2 gap-2 px-1 max-h-[75dvh] overflow-y-auto'>
					{/* Thông tin cá nhân */}
					<div className='col-span-1 md:col-span-2'>
						<h3 className='font-semibold text-lg text-red-600'>Thông tin cá nhân</h3>
					</div>
					<div className='space-y-1'>
						<Label className='required'>Họ và tên</Label>
						<NgocInput placeholder='Nhập họ tên' maxLength={255} {...register("studentName")} />

						<ZodError message={errors.studentName?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Mã sinh viên</Label>
						<NgocInput placeholder='Nhập mã sinh viên' {...register("studentId")} maxLength={40} autoComplete='off' />

						<ZodError message={errors.studentId?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số CCCD </Label>
						<NgocInput placeholder='Nhập số CCCD' {...register("cccd")} maxLength={12} autoComplete='off' />

						<ZodError message={errors.cccd?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngày sinh </Label>
						<Controller
							control={control}
							name='dateOfBirth'
							render={({ field }) => (
								<Popover open={openDateOfBirthSelected} onOpenChange={(value) => { setOpenDateOfBirthSelected(value); if (!value) trigger("dateOfBirth"); }}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày sinh" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												if (value) {
													field.onChange(getLocalDate(value));
													setOpenDateOfBirthSelected(false);
												}
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>

						<ZodError message={errors.dateOfBirth?.message} />
					</div>

					{/* Thông tin văn bằng */}
					<div className='col-span-1 md:col-span-2 mt-4'>
						<h3 className='font-semibold text-lg text-red-600'>Thông tin Khen thưởng - Chứng nhận</h3>
					</div>
					<div className='space-y-1'>
						<Label className='required'>Năm học</Label>
						<NgocInput placeholder='Nhập năm học (ví dụ: 2025-2026)' {...register("year")} maxLength={20} autoComplete='off' />

						<ZodError message={errors.year?.message} />
					</div>
					<div className='space-y-1'>
						<Label className=''>Học kỳ</Label>
						<NgocInput placeholder='Nhập học kỳ (ví dụ: Học kỳ 2)' {...register("semester")} maxLength={255} autoComplete='off' />

						<ZodError message={errors.semester?.message} />
					</div>

					{/* <div className='space-y-1'>
						<Label className=''>Điểm trung bình</Label>
						<Controller
						control={control}
						name="gpa"
						render={({ field }) => (
							<Input
							value={field.value}
							onChange={(e) => {
								field.onChange(e.target.value);
								setTimeout(() => {
									field.onChange(formatScore(e.target.value));
								}, 1000);
							}}
							type="number"
							onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
							placeholder="Nhập điểm trung bình"
							autoComplete="off"
							/>
						)}
						/>
						<ZodError message={errors.gpa?.message} />
					</div> */}

					{/* <div className='space-y-1'>
						<Label className=''>Điểm rèn luyện</Label>
						<Input
							type='number'
							placeholder='Nhập điểm rèn luyện'
							{...register("conductScore")}
							autoComplete='off'
							onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
						/>
						<ZodError message={errors.conductScore?.message} />
					</div> */}

					<div className='space-y-1'>
						<Label className='required'>Ngành học</Label>
						{/* <Input placeholder='Nhập mã ngành' {...register("majorCode")} autoComplete='off' /> */}
						<Controller
							control={control}
							name='major'
							render={({ field }) => (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant='outline' className={cn("w-full justify-between text-slate-700 font-normal")}>
											{getValues("major") == "" ? "Chọn ngành" : `${getValues("major")}`}
											<ChevronDown />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										<div className='flex gap-1'>
											<input
												value={searchMajorName}
												onInput={(e: any) => {
													setSearchMajorName(e.target.value);
												}}
												type='text'
												className='border w-full px-1'
												placeholder='Tìm kiếm...'
												onKeyDown={(e) => {
													e.stopPropagation();
												}}
											/>
											<button
												onClick={() => {
													setSearchMajorName("");
												}}
												type='button'
												className='cursor-pointer text-sm'
											>
												<RefreshCw size={"1rem"} />
											</button>
										</div>
										<div className='h-40 overflow-y-scroll'>
											{majorListForScholarship.filter((majorName) => majorName.toLocaleLowerCase().includes(searchMajorName.trim().toLowerCase()))
												.map((majorName) => {
													return (
														<DropdownMenuCheckboxItem
															key={majorName}
															checked={getValues("major") === majorName}
															onCheckedChange={() => {
																field.onChange(majorName);
																const targetMajor = majorListForScholarship.find((smj) => smj === majorName);
																setValue("major", targetMajor || "");
															}}
														>
															{majorName}
														</DropdownMenuCheckboxItem>
													);
												})}
										</div>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						/>

						<ZodError message={errors.major?.message} />
					</div>

					<div className='space-y-1'>
						<Label className=''>Mã KT-CN</Label>
						<NgocInput placeholder='Nhập mã KT-CN' {...register("scholarshipCode")} maxLength={255} autoComplete='off' />
						<ZodError message={errors.scholarshipCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Tên KT-CN</Label>
						<NgocInput placeholder='Nhập tên KT-CN' {...register("scholarshipName")} maxLength={255} autoComplete='off' />
						<ZodError message={errors.scholarshipName?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Danh hiệu</Label>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='outline' className={cn("justify-between text-slate-700 font-normal w-full")}>
									{selectedTitle ? selectedTitle : "Danh hiệu"}
									<ChevronDown />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<div className='flex gap-1'>
									<input
										value={searchScholarshipTitleName}
										onInput={(e: any) => {
											setSearchScholarshipTitleName(e.target.value);
										}}
										type='text'
										className='border w-full px-1'
										placeholder='Tìm kiếm...'
										onKeyDown={(e) => {
											e.stopPropagation();
										}}
									/>
									<button
										onClick={() => {
											setSearchScholarshipTitleName("");
										}}
										type='button'
										className='cursor-pointer text-sm'
									>
										<RefreshCw size={"1rem"} />
									</button>
								</div>
								<div className='h-fit max-h-60 overflow-y-scroll'>
									{scholarshipTitleOptions
										.filter((title) => title.toLocaleLowerCase().includes(searchScholarshipTitleName.trim().toLowerCase()))
										.map((titleName) => (
											<DropdownMenuCheckboxItem
												key={titleName}
												checked={selectedTitle === titleName}
												onCheckedChange={() => {
													if (titleName == "Khác") {
														setValue("title", "");
													} else {
														setValue("title", titleName);
														trigger("title");
													}
													setSelectedTitle(titleName);
												}}
												className='cursor-pointer'
											>
												{titleName}
											</DropdownMenuCheckboxItem>
										))}
								</div>
							</DropdownMenuContent>
						</DropdownMenu>
						{
							selectedTitle === "Khác" && (
								<NgocInput
									placeholder='Nhập danh hiệu'
									{...register("title")}
									maxLength={250}
								/>
							)
						}

						<ZodError message={errors.title?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label className=''>Số tiền Khen thưởng - Chứng nhận</Label>
						<Controller
						control={control}
						name="amount"
						render={({ field }) => (
							<Input
							value={formatMoneyVN(field.value)}
							onChange={(e) => {
								field.onChange(formatMoneyVN(e.target.value));
							}}
							onKeyDown={(evt) => ["e", "E", "+", "-"].includes(evt.key) && evt.preventDefault()}
							placeholder="Nhập số tiền Khen thưởng - Chứng nhận"
							autoComplete="off"
							/>
						)}
						/>

						<ZodError message={errors.amount?.message} />
					</div> */}
					<div className='space-y-1'>
						<Label className='required'>Số quyết định</Label>
						<NgocInput placeholder='Nhập số quyết định' {...register("certificateNo")} maxLength={255} autoComplete='off' />
						<ZodError message={errors.certificateNo?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label className=''>Mức PT</Label>
						<NgocInput placeholder='Nhập mức PT' {...register("percentLevel")} maxLength={255} autoComplete='off' />
						<ZodError message={errors.percentLevel?.message} />
					</div> */}

					<div className='col-span-1 md:col-span-2 space-y-1'>
						<Label>Ghi chú</Label>
						<NgocInput rows={3} placeholder='Nhập ghi chú' {...register("notes")} maxLength={500} />

						<ZodError message={errors.notes?.message} />
					</div>
				</form>
				<div className='flex justify-end gap-2'>
					<Button
						variant='destructive'
						onClick={() => {
							onClose();
						}}
					>
						Hủy
					</Button>
					<Button disabled={!isValid} variant={"add"} type='submit' form='handleScholarshipForm' className="select-none">
						Lưu
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
});
